import { useEffect, useMemo, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import { listWinners, downloadWinnersXlsx, type Winner } from '../lib/admin';

// Two payout routes. `pickup` = collect in person at Matasci (Ticino
// winners); `shipping` = we post it, so we need a delivery address.
//
// Derived from user_profiles.canton, which is trustworthy here: the 2024
// edition was Ticino-only, so migration 00033's bulk 'TI' backfill
// reflects reality. Still flippable per row for the odd exception.
type Variant = 'pickup' | 'shipping';

const LOCALES = ['it', 'de', 'fr', 'en'] as const;
type Locale = (typeof LOCALES)[number];

function normLocale(raw: string): Locale {
  const s = (raw || '').slice(0, 2).toLowerCase();
  return (LOCALES as readonly string[]).includes(s) ? (s as Locale) : 'it';
}

function prizeLabel(w: Winner): string {
  if (!w.prizeTitle) return '';
  return w.prizeStock ? `${w.prizeTitle} × ${w.prizeStock}` : w.prizeTitle;
}

const PICKUP_ADDRESS = 'Matasci Vini SA — Wine Store\nVia Verbano 6, 6598 Tenero-Contra';

// Email copy, per locale × variant. Kept as plain strings rather than
// going through the i18n JSONs on purpose: these are operator-facing
// drafts to paste into a mail client, not shipped UI strings, and the
// admin console is English-only.
function buildEmail(w: Winner, locale: Locale, variant: Variant): { subject: string; body: string } {
  const prize = prizeLabel(w) || '—';
  const n = w.rank;

  if (locale === 'de') {
    return {
      subject: `BeerCup 2026 — du hast gewonnen! (Platz ${n})`,
      body: `Hallo ${w.nickname},

herzlichen Glückwunsch! Du hast den BeerCup 2026 auf Platz ${n} der Schweizer Rangliste abgeschlossen.

Dein Preis: ${prize}

Damit wir dir den Preis übergeben können, brauchen wir ein paar Angaben. Bitte antworte auf diese E-Mail mit:
• Vor- und Nachname
• Adresse
• Geburtsdatum
${variant === 'pickup'
  ? `
Abholung des Preises:
${PICKUP_ADDRESS}
Während der Öffnungszeiten.

Falls du den Preis nicht selbst abholst, teile uns bitte den Vor- und Nachnamen der abholenden Person mit. Bei der Abholung wird ein gültiger Ausweis verlangt.`
  : `
Versand:
Da du ausserhalb des Tessins wohnst, schicken wir dir den Preis zu. Bitte gib uns die Lieferadresse an, falls sie von deiner Adresse abweicht.`}

Danke fürs Mitspielen und bis bald!
Das BeerCup-Team`,
    };
  }

  if (locale === 'fr') {
    return {
      subject: `BeerCup 2026 — tu as gagné ! (place ${n})`,
      body: `Bonjour ${w.nickname},

félicitations ! Tu termines la BeerCup 2026 à la ${n}e place du classement suisse.

Ton prix : ${prize}

Pour te remettre ton prix, nous avons besoin de quelques informations. Merci de répondre à cet e-mail en indiquant :
• Nom et prénom
• Adresse
• Date de naissance
${variant === 'pickup'
  ? `
Retrait du prix :
${PICKUP_ADDRESS}
Pendant les heures d'ouverture.

Si ce n'est pas toi qui viens retirer le prix, indique-nous le nom et prénom de la personne qui viendra. Une pièce d'identité valable sera demandée lors du retrait.`
  : `
Expédition :
Comme tu habites hors du Tessin, nous t'enverrons le prix par la poste. Merci de nous indiquer l'adresse de livraison si elle diffère de la tienne.`}

Merci d'avoir joué et à bientôt !
L'équipe BeerCup`,
    };
  }

  if (locale === 'en') {
    return {
      subject: `BeerCup 2026 — you won! (position ${n})`,
      body: `Hi ${w.nickname},

congratulations! You finished BeerCup 2026 in position ${n} of the Swiss leaderboard.

Your prize: ${prize}

To hand over your prize we need a few details. Please reply to this email with:
• First and last name
• Address
• Date of birth
${variant === 'pickup'
  ? `
Prize collection:
${PICKUP_ADDRESS}
During opening hours.

If you're not collecting the prize yourself, let us know the first and last name of the person who will. A valid ID will be required at collection.`
  : `
Shipping:
As you live outside Ticino, we'll ship the prize to you. Please also give us the delivery address if it differs from your own.`}

Thanks for playing and see you soon!
The BeerCup team`,
    };
  }

  return {
    subject: `BeerCup 2026 — hai vinto! (posizione ${n})`,
    body: `Ciao ${w.nickname},

complimenti! Hai chiuso la BeerCup 2026 al ${n}° posto della classifica svizzera.

Il tuo premio: ${prize}

Per consegnartelo ci servono alcuni dati. Rispondi a questa email indicando:
• Nome e cognome
• Indirizzo
• Data di nascita
${variant === 'pickup'
  ? `
Ritiro del premio:
${PICKUP_ADDRESS}
Durante gli orari di apertura.

Se non sei tu a ritirare il premio, indicaci nome e cognome della persona che verrà a ritirarlo. Al ritiro verrà richiesto un documento d'identità valido.`
  : `
Spedizione:
Dato che sei fuori dal Ticino, ti spediamo il premio. Indicaci anche l'indirizzo di spedizione, se diverso dal tuo.`}

Grazie per aver giocato e a presto!
Il team BeerCup`,
  };
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function AdminWinners() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [winners, setWinners] = useState<Winner[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [limit, setLimit] = useState(22);
  const [exporting, setExporting] = useState(false);
  // Per-winner overrides, keyed by userId. Absent = use the default
  // derived from canton / users.locale.
  const [variants, setVariants] = useState<Record<string, Variant>>({});
  const [locales, setLocales] = useState<Record<string, Locale>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  async function load() {
    if (!accessToken) return;
    setErr(null);
    try {
      setWinners(await listWinners(accessToken, { limit }));
    } catch (e: any) {
      setErr(`${e?.message || e}`);
    }
  }

  useEffect(() => { load(); }, [accessToken, limit]);

  async function exportXlsx() {
    if (!accessToken || exporting) return;
    setErr(null);
    setExporting(true);
    try {
      await downloadWinnersXlsx(accessToken, { limit });
    } catch (e: any) {
      setErr(`Export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  }

  function variantOf(w: Winner): Variant {
    return variants[w.userId] ?? (w.canton === 'TI' ? 'pickup' : 'shipping');
  }
  function localeOf(w: Winner): Locale {
    return locales[w.userId] ?? normLocale(w.locale);
  }

  async function flash(key: string, text: string) {
    if (await copy(text)) {
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } else {
      setErr('Clipboard blocked by the browser — select the text and copy manually.');
    }
  }

  const allEmails = useMemo(
    () => (winners ?? []).map((w) => w.email).join(', '),
    [winners],
  );

  const missingPrize = useMemo(
    () => (winners ?? []).filter((w) => !w.prizeTitle).length,
    [winners],
  );

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Winners</h1>
          </header>

          <p class="admin-lede">
            Top {limit} of the Switzerland-wide leaderboard with the prize
            assigned to each position on the{' '}
            <a class="admin-link" href={localePath('/admin/prizes')}>leaderboard prizes</a>{' '}
            page, plus each winner's email and language. Open a row to get a
            ready-to-send message asking for the details we need to hand the
            prize over.
          </p>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'rgba(245,237,224,0.55)' }}>
              Top{' '}
              <input
                type="number"
                min={1}
                max={500}
                value={limit}
                onInput={(e) => {
                  const v = parseInt((e.target as HTMLInputElement).value, 10);
                  if (!Number.isNaN(v) && v > 0 && v <= 500) setLimit(v);
                }}
                style={{
                  width: 64, padding: '4px 8px', background: '#0a0705', color: '#f5ede0',
                  border: '1px solid rgba(245,237,224,0.15)', borderRadius: 6,
                  fontFamily: 'IBM Plex Mono, Menlo, monospace', fontSize: 12,
                }}
              />
            </label>
            <button class="admin-link" onClick={() => flash('all', allEmails)}>
              {copied === 'all' ? 'Copied ✓' : 'Copy all emails'}
            </button>
            <button class="admin-primary" onClick={exportXlsx} disabled={exporting}>
              {exporting ? 'Exporting…' : `Export ${limit} to .xlsx`}
            </button>
            <button class="admin-link" onClick={load}>Reload</button>
          </div>

          {err && <p class="admin-error">{err}</p>}
          {missingPrize > 0 && (
            <p class="admin-error">
              {missingPrize} position{missingPrize === 1 ? '' : 's'} in this
              list has no prize assigned yet.
            </p>
          )}

          {winners === null ? (
            <p class="admin-lede">Loading…</p>
          ) : winners.length === 0 ? (
            <p class="admin-lede">No ranked users yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {winners.map((w) => {
                const variant = variantOf(w);
                const locale = localeOf(w);
                const mail = buildEmail(w, locale, variant);
                const isOpen = open === w.userId;
                return (
                  <section
                    key={w.userId}
                    style={{
                      background: '#120b07',
                      border: '1px solid rgba(245,237,224,0.1)',
                      borderRadius: 10,
                      padding: 14,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <strong style={{ fontFamily: 'IBM Plex Mono, Menlo, monospace', minWidth: 34 }}>
                        #{w.rank}
                      </strong>
                      <strong>{w.nickname}</strong>
                      <span style={{ fontSize: 13, color: 'rgba(245,237,224,0.7)' }}>{w.email}</span>
                      <span style={{ fontSize: 12, color: 'rgba(245,237,224,0.45)' }}>
                        {normLocale(w.locale)} · {w.canton || '—'} · {w.pts} pts · {w.picks} picks
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 13 }}>
                        {w.prizeTitle
                          ? <>{prizeLabel(w)}{w.prizeValueChf != null ? ` · CHF ${w.prizeValueChf.toFixed(2)}` : ''}</>
                          : <em style={{ color: '#e06b5a' }}>no prize assigned</em>}
                      </span>
                      <button class="admin-link" onClick={() => setOpen(isOpen ? null : w.userId)}>
                        {isOpen ? 'Hide' : 'Email'}
                      </button>
                    </div>

                    {isOpen && (
                      <div style={{ marginTop: 12, borderTop: '1px solid rgba(245,237,224,0.08)', paddingTop: 12 }}>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                          <select
                            value={variant}
                            onChange={(e) =>
                              setVariants((m) => ({ ...m, [w.userId]: (e.target as HTMLSelectElement).value as Variant }))
                            }
                            style={selectStyle}
                          >
                            <option value="pickup">Pickup at Matasci (Ticino)</option>
                            <option value="shipping">Shipping (outside Ticino)</option>
                          </select>
                          <select
                            value={locale}
                            onChange={(e) =>
                              setLocales((m) => ({ ...m, [w.userId]: (e.target as HTMLSelectElement).value as Locale }))
                            }
                            style={selectStyle}
                          >
                            {LOCALES.map((l) => <option value={l}>{l.toUpperCase()}</option>)}
                          </select>
                          <button class="admin-link" onClick={() => flash(`e:${w.userId}`, w.email)}>
                            {copied === `e:${w.userId}` ? 'Copied ✓' : 'Copy address'}
                          </button>
                          <button class="admin-link" onClick={() => flash(`s:${w.userId}`, mail.subject)}>
                            {copied === `s:${w.userId}` ? 'Copied ✓' : 'Copy subject'}
                          </button>
                          <button class="admin-primary" onClick={() => flash(`b:${w.userId}`, mail.body)}>
                            {copied === `b:${w.userId}` ? 'Copied ✓' : 'Copy message'}
                          </button>
                          <a
                            class="admin-link"
                            href={`mailto:${encodeURIComponent(w.email)}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`}
                          >
                            Open in mail app
                          </a>
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(245,237,224,0.5)', marginBottom: 6 }}>
                          Subject: {mail.subject}
                        </div>
                        <textarea
                          readOnly
                          value={mail.body}
                          rows={20}
                          onFocus={(e) => (e.target as HTMLTextAreaElement).select()}
                          style={{
                            width: '100%', background: '#0a0705', color: '#f5ede0',
                            border: '1px solid rgba(245,237,224,0.12)', borderRadius: 8,
                            padding: 12, fontFamily: 'IBM Plex Mono, Menlo, monospace',
                            fontSize: 12, lineHeight: 1.55, resize: 'vertical',
                          }}
                        />
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}

          <a class="admin-back" href={localePath('/admin')}>← Admin</a>
        </div>
      </div>
    </RequireAdmin>
  );
}

const selectStyle: any = {
  padding: '5px 8px',
  background: '#0a0705',
  color: '#f5ede0',
  border: '1px solid rgba(245,237,224,0.15)',
  borderRadius: 6,
  fontSize: 12,
};
